import React from 'react';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useChatbot } from '../contexts/ChatbotContext';

interface ChatbotLimitsState {
  messageLimit: number;
  conversationLimit: number;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchLimits: (chatbotPublicId: string) => Promise<void>;
  updateMessageLimit: (chatbotPublicId: string, limit: number) => Promise<boolean>;
  updateConversationLimit: (chatbotPublicId: string, limit: number) => Promise<boolean>;
  checkMessageLimit: (messageCount: number) => boolean;
  checkConversationLimit: (conversationCount: number) => boolean;
}

export const useChatbotLimits = create<ChatbotLimitsState>((set, get) => ({
  messageLimit: 100,
  conversationLimit: 100,
  loading: false,
  error: null,
  initialized: false,

  fetchLimits: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });
      
      const { data, error } = await supabase
        .from('chatbot_configs')
        .select('settings')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (error) throw error;

      // Extract limits from settings
      const settings = data?.settings || {};
      const messageLimit = settings.message_limit || 100;
      const conversationLimit = settings.conversation_limit || 100;

      set({ 
        messageLimit, 
        conversationLimit,
        initialized: true,
        loading: false 
      });
    } catch (err) {
      console.error('Error fetching chatbot limits:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Failed to load chatbot limits',
        loading: false
      });
    }
  },

  updateMessageLimit: async (chatbotPublicId, limit) => {
    try {
      set({ loading: true, error: null });

      // Get current settings first
      const { data: currentData, error: fetchError } = await supabase
        .from('chatbot_configs')
        .select('settings')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      // Prepare updated settings
      const currentSettings = currentData?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        message_limit: limit
      };

      // Update settings
      const { error: updateError } = await supabase
        .from('chatbot_configs')
        .update({ settings: updatedSettings })
        .eq('chatbot_public_id', chatbotPublicId);

      if (updateError) throw updateError;

      set({ messageLimit: limit, loading: false });
      return true;
    } catch (err) {
      console.error('Error updating message limit:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Failed to update message limit',
        loading: false
      });
      return false;
    }
  },

  updateConversationLimit: async (chatbotPublicId, limit) => {
    try {
      set({ loading: true, error: null });

      // Get current settings first
      const { data: currentData, error: fetchError } = await supabase
        .from('chatbot_configs')
        .select('settings')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      // Prepare updated settings
      const currentSettings = currentData?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        conversation_limit: limit
      };

      // Update settings
      const { error: updateError } = await supabase
        .from('chatbot_configs')
        .update({ settings: updatedSettings })
        .eq('chatbot_public_id', chatbotPublicId);

      if (updateError) throw updateError;

      set({ conversationLimit: limit, loading: false });
      return true;
    } catch (err) {
      console.error('Error updating conversation limit:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Failed to update conversation limit',
        loading: false
      });
      return false;
    }
  },

  checkMessageLimit: (messageCount) => {
    const { messageLimit } = get();
    return messageCount < messageLimit;
  },

  checkConversationLimit: (conversationCount) => {
    const { conversationLimit } = get();
    return conversationCount < conversationLimit;
  }
}));

// Hook for easier use in components
export function useChatbotLimitsHook() {
  const { chatbotPublicId } = useChatbot();
  const {
    messageLimit,
    conversationLimit,
    loading,
    error,
    initialized,
    fetchLimits,
    updateMessageLimit,
    updateConversationLimit,
    checkMessageLimit,
    checkConversationLimit
  } = useChatbotLimits();

  // Initialize limits when component mounts
  React.useEffect(() => {
    if (chatbotPublicId && !initialized) {
      fetchLimits(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, fetchLimits]);

  return {
    messageLimit,
    conversationLimit,
    loading,
    error,
    initialized,
    updateMessageLimit: (limit: number) => updateMessageLimit(chatbotPublicId || '', limit),
    updateConversationLimit: (limit: number) => updateConversationLimit(chatbotPublicId || '', limit),
    checkMessageLimit,
    checkConversationLimit
  };
}